// src/pages/firstaider/IncidentPage.tsx — States 4/5/6: briefing, ETA picker,
// and the shared group thread.
//
// Route: /first-aider/incidents/:id. The same page serves the caller, every
// responder, and any admin — what they see depends on their relationship to
// the incident:
//   - a recipient who hasn't responded → briefing card + response buttons.
//   - "On my way" → ETA picker → thread.
//   - "Cannot attend" → thread (their row shows declined).
//   - the caller / a responder who's already acted → thread.
//
// Polls every 10s while the incident is active (gotcha #14 — not faster, no
// WebSockets). Marks the alert read on first open (fire-and-forget). The ONE
// confirmation modal in the whole feature guards "Mark resolved" (gotcha #12).

import type { CSSProperties } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { C, F } from "../../theme";
import { useCurrentUser } from "../../auth";
import {
  FA,
  THREAD_POLL_MS,
  ETA_VALUES,
  callerName,
  clockHM,
  elapsedShort,
  etaLabel,
  fetchIncident,
  patchAlert,
  resolveIncident,
  type EtaMinutes,
  type Incident,
  type Responder,
  type Tally,
} from "../../lib/firstAider";
import {
  CheckGlyph,
  CrossGlyph,
  FaKeyframes,
  FaTopBar,
  PulseDot,
} from "../../components/firstAider/kit";

const ME_REFRESH_EVENT = "nymr-me-refresh";

type View = "briefing" | "eta" | "thread";

export function IncidentPage() {
  const params = useParams<{ id: string }>();
  const incidentId = Number(params.id);
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const [incident, setIncident] = useState<Incident | null>(null);
  const [responders, setResponders] = useState<Responder[]>([]);
  const [tally, setTally] = useState<Tally>({ onWay: 0, cannot: 0, pending: 0 });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const readPatchedRef = useRef(false);
  const viewInitialisedRef = useRef(false);

  const myAlert = me?.hubUserId != null
    ? responders.find((r) => r.user.id === me.hubUserId) ?? null
    : null;
  const iAmCaller = !!(me?.hubUserId != null && incident?.caller.id === me.hubUserId);
  const canResolve = iAmCaller || !!me?.isAdmin;

  const load = useCallback(async () => {
    const res = await fetchIncident(incidentId);
    setIncident(res.incident);
    setResponders(res.responders);
    setTally(res.tally);
    return res;
  }, [incidentId]);

  // Initial load.
  useEffect(() => {
    if (!Number.isSafeInteger(incidentId) || incidentId <= 0) {
      setLoadError("Invalid incident.");
      return;
    }
    let cancelled = false;
    load().catch((e) => {
      if (!cancelled) setLoadError(e?.message ?? "Couldn't load the callout.");
    });
    return () => {
      cancelled = true;
    };
  }, [incidentId, load]);

  // Decide the initial view once me + incident + responders have all landed.
  useEffect(() => {
    if (viewInitialisedRef.current) return;
    if (!incident || me === null) return;
    const mine = me.hubUserId != null ? responders.find((r) => r.user.id === me.hubUserId) ?? null : null;
    const resolved = incident.status !== "active";
    // If I'm a first aider who hasn't responded yet (pending/read) — INCLUDING
    // when I'm also the caller — open on the briefing so I can confirm my
    // attendance. Otherwise (already responded, no alert, or resolved) open the
    // thread, where the caller/admin can mark it resolved.
    const unresponded = !!mine && (mine.status === "pending" || mine.status === "read");
    setView(!resolved && unresponded ? "briefing" : "thread");
    viewInitialisedRef.current = true;
  }, [incident, responders, me]);

  // Mark read on first open (fire-and-forget) for a recipient who hasn't yet.
  useEffect(() => {
    if (readPatchedRef.current) return;
    if (!myAlert) return;
    if (myAlert.status === "pending") {
      readPatchedRef.current = true;
      patchAlert(myAlert.alertId, { readAt: true }).catch(() => {
        readPatchedRef.current = false;
      });
    }
  }, [myAlert]);

  // Poll every 10s while the incident is active.
  useEffect(() => {
    if (!incident || incident.status !== "active") return;
    const id = window.setInterval(() => {
      load().catch(() => {
        /* transient — keep the last good render, try again next tick */
      });
    }, THREAD_POLL_MS);
    return () => window.clearInterval(id);
  }, [incident, load]);

  // Toast auto-dismiss.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const signalMeRefresh = () => window.dispatchEvent(new Event(ME_REFRESH_EVENT));

  async function onDecline() {
    if (!myAlert || busy) return;
    setBusy(true);
    try {
      await patchAlert(myAlert.alertId, { status: "declined" });
      await load();
      setView("thread");
      setToast("You're not attending. Other responders notified.");
      signalMeRefresh();
    } catch {
      setToast("Couldn't record that. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmEta(eta: EtaMinutes) {
    if (!myAlert || busy) return;
    setBusy(true);
    try {
      await patchAlert(myAlert.alertId, { status: "on_way", etaMinutes: eta });
      await load();
      setView("thread");
      setToast(`Responding — ${etaLabel(eta)} ETA. Other responders notified.`);
      signalMeRefresh();
    } catch {
      setToast("Couldn't record that. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onResolve() {
    if (busy) return;
    setBusy(true);
    try {
      await resolveIncident(incidentId);
      await load();
      setToast("Incident marked resolved.");
      signalMeRefresh();
    } catch {
      // Most likely 409 already-resolved (someone beat us). Refetch shows the
      // resolved state; surface a gentle note.
      await load().catch(() => undefined);
      setToast("Already resolved.");
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div style={{ background: FA.tint, minHeight: "100vh" }}>
        <FaTopBar title="First aider callout" sub="Emergency dispatch" onBack={() => navigate("/")} pulsing={false} />
        <div style={{ padding: 24 }}>
          <div style={errorCardStyle}>{loadError}</div>
        </div>
      </div>
    );
  }

  if (!incident || view === null) {
    return (
      <div style={{ background: FA.tint, minHeight: "100vh" }}>
        <FaTopBar title="First aider callout" sub="Emergency dispatch" onBack={() => navigate("/")} />
        <div style={{ padding: 16 }}>
          <div style={{ ...skeletonStyle, height: 180 }} />
        </div>
      </div>
    );
  }

  const subLabel =
    incident.status === "active"
      ? `Active · ${clockHM(incident.createdAt)}`
      : `Resolved · ${incident.resolvedAt ? clockHM(incident.resolvedAt) : clockHM(incident.createdAt)}`;

  return (
    <div style={{ background: FA.tint, minHeight: "100vh" }}>
      <FaTopBar
        title="First aider callout"
        sub={subLabel}
        onBack={() => navigate("/")}
        pulsing={incident.status === "active"}
      />

      <div style={{ padding: "14px 16px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
        {view === "briefing" && (
          <>
            <BriefingCard incident={incident} />
            <ResponseButtons busy={busy} onCannot={onDecline} onGo={() => setView("eta")} />
            <LiveMiniTally tally={tally} />
            <button type="button" onClick={() => setView("thread")} style={seeThreadBtnStyle}>
              See all responders {canResolve ? "/ mark resolved " : ""}→
            </button>
          </>
        )}

        {view === "eta" && (
          <>
            <BriefingCard incident={incident} compact />
            <EtaPicker busy={busy} onConfirm={onConfirmEta} onCancel={onDecline} />
          </>
        )}

        {view === "thread" && (
          <>
            <ThreadSummary incident={incident} tally={tally} />
            <ResponderList responders={responders} />
            {incident.status === "active" && canResolve && (
              <ResolveButton busy={busy} onResolve={onResolve} />
            )}
            {incident.status !== "active" && (
              <div style={resolvedNoteStyle}>
                {incident.status === "auto_resolved"
                  ? "Incident auto-resolved after 24h. If this is in error, post a new callout."
                  : `Resolved${incident.resolvedBy ? ` by ${callerName(incident.resolvedBy)}` : ""}${
                      incident.resolvedAt ? ` at ${clockHM(incident.resolvedAt)}` : ""
                    }.`}
              </div>
            )}
          </>
        )}
      </div>

      {toast && (
        <div role="status" aria-live="polite" style={toastStyle}>
          {toast}
        </div>
      )}
      <FaKeyframes />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Briefing card (State 4) — the structured field table
// ---------------------------------------------------------------------------

function BriefingCard({ incident, compact = false }: { incident: Incident; compact?: boolean }) {
  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div style={cardEyebrowStyle}>
          <PulseDot tone="white" />
          First aider callout · {clockHM(incident.createdAt)}
        </div>
        <div style={cardHeadlineStyle}>{incident.location}</div>
      </div>
      <div style={{ padding: compact ? "10px 16px 12px" : "12px 16px 16px" }}>
        <FieldRow label="Casualty" value={incident.casualty} />
        <FieldRow label="Nature" value={incident.nature} />
        <FieldRow
          label="Life-threatening"
          badge={incident.isLifeThreatening ? { text: "YES", tone: "red" } : { text: "No", tone: "mute" }}
        />
        <FieldRow
          label="999 called"
          badge={
            incident.called999
              ? { text: `YES · ${clockHM(incident.createdAt)}`, tone: "red" }
              : { text: "No", tone: "mute" }
          }
        />
        <FieldRow
          label="Reported by"
          value={callerName(incident.caller)}
          phone={incident.caller.deleted ? null : incident.caller.phone}
          last
        />
      </div>
    </div>
  );
}

function FieldRow({
  label,
  value,
  badge,
  phone,
  last = false,
}: {
  label: string;
  value?: string;
  badge?: { text: string; tone: "red" | "mute" };
  phone?: string | null;
  last?: boolean;
}) {
  return (
    <div style={{ ...fieldRowStyle, borderBottom: last ? "none" : `1px solid ${C.hairlineLt}` }}>
      <div style={fieldRowLabelStyle}>{label}</div>
      <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
        {badge ? (
          <span style={badge.tone === "red" ? badgeRedStyle : badgeMuteStyle}>{badge.text}</span>
        ) : (
          <>
            <div style={fieldRowValueStyle}>{value}</div>
            {phone && (
              <a href={`tel:${phone}`} style={phoneLinkStyle}>
                {phone}
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Response buttons (State 4) + live mini tally
// ---------------------------------------------------------------------------

function ResponseButtons({
  busy,
  onCannot,
  onGo,
}: {
  busy: boolean;
  onCannot: () => void;
  onGo: () => void;
}) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <button type="button" onClick={onCannot} disabled={busy} style={cannotBtnStyle}>
        <CrossGlyph size={15} color={FA.stop} />
        Cannot attend
      </button>
      <button type="button" onClick={onGo} disabled={busy} style={goBtnStyle}>
        <CheckGlyph size={15} color="#FFFFFF" />
        On my way
      </button>
    </div>
  );
}

function LiveMiniTally({ tally }: { tally: Tally }) {
  const parts: string[] = [];
  if (tally.onWay > 0) parts.push(`${tally.onWay} already on the way`);
  if (tally.cannot > 0) parts.push(`${tally.cannot} declined`);
  parts.push(`${tally.pending} pending`);
  return <div style={miniTallyStyle}>{parts.join(" · ")}</div>;
}

// ---------------------------------------------------------------------------
// ETA picker (State 5)
// ---------------------------------------------------------------------------

function EtaPicker({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: (eta: EtaMinutes) => void;
  onCancel: () => void;
}) {
  const [eta, setEta] = useState<EtaMinutes | null>(null);
  return (
    <>
      <div style={pickerStyle} role="radiogroup" aria-label="ETA in minutes">
        <div style={pickerEyebrowStyle}>
          <CheckGlyph size={12} color={FA.go} />
          You're on your way — what's your ETA?
        </div>
        <div style={pickerChipsStyle}>
          {ETA_VALUES.map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={eta === v}
              onClick={() => setEta(v)}
              style={eta === v ? etaChipSelStyle : etaChipStyle}
            >
              {etaLabel(v)}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => eta != null && onConfirm(eta)}
          disabled={eta == null || busy}
          style={{ ...etaConfirmStyle, opacity: eta == null || busy ? 0.5 : 1, cursor: eta == null ? "not-allowed" : "pointer" }}
        >
          <CheckGlyph size={16} color="#FFFFFF" />
          {eta != null ? `Confirm — ${etaLabel(eta)} ETA` : "Select an ETA above"}
        </button>
      </div>
      <button type="button" onClick={onCancel} disabled={busy} style={etaEscapeStyle}>
        Change to "Cannot attend"
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Thread summary (State 6) — incident line + tally
// ---------------------------------------------------------------------------

function ThreadSummary({ incident, tally }: { incident: Incident; tally: Tally }) {
  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div style={cardEyebrowRowStyle}>
          <div style={cardEyebrowStyle}>
            <PulseDot tone="white" />
            {incident.status === "active" ? "Active callout" : "Resolved"}
          </div>
          <div style={cardTimeStyle}>
            {clockHM(incident.createdAt)} · {elapsedShort(incident.createdAt)}
          </div>
        </div>
        <div style={cardHeadlineStyle}>{incident.location}</div>
      </div>
      <div style={{ padding: "12px 16px 14px" }}>
        <div style={summaryLineStyle}>
          {incident.nature}
          {incident.isLifeThreatening ? " · Life-threatening" : ""}
          {incident.called999 ? " · 999 called" : ""}.
        </div>
        <div style={tallyRowStyle}>
          <TallyCol count={tally.onWay} label="On the way" color={FA.go} />
          <TallyCol count={tally.cannot} label="Cannot" color={FA.stop} />
          <TallyCol count={tally.pending} label="Pending" color={FA.pending} />
        </div>
        <span style={srOnlyStyle}>
          {tally.onWay} first aiders on the way, {tally.cannot} cannot attend, {tally.pending} pending.
        </span>
      </div>
    </div>
  );
}

function TallyCol({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div style={{ flex: 1, textAlign: "center" }}>
      <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 700, letterSpacing: -0.4, lineHeight: 1, color }}>
        {count}
      </div>
      <div
        style={{
          fontFamily: F.body,
          fontSize: 9.5,
          fontWeight: 800,
          letterSpacing: 0.9,
          textTransform: "uppercase",
          marginTop: 4,
          color,
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Responder list (State 6)
// ---------------------------------------------------------------------------

function ResponderList({ responders }: { responders: Responder[] }) {
  const visible = responders.slice(0, 6);
  const overflow = responders.length - visible.length;
  return (
    <div style={listStyle}>
      {visible.map((r) => (
        <ResponderRow key={r.alertId} r={r} />
      ))}
      {overflow > 0 && (
        <div style={overflowRowStyle}>+{overflow} more pending — tap to view</div>
      )}
      {responders.length === 0 && (
        <div style={emptyResponderStyle}>No first aiders alerted.</div>
      )}
    </div>
  );
}

function ResponderRow({ r }: { r: Responder }) {
  const tone = r.status === "on_way" ? FA.go : r.status === "declined" ? FA.stop : FA.pending;
  const pending = r.status === "pending" || r.status === "read";
  const name = `${r.user.firstName ?? ""} ${r.user.lastName ?? ""}`.trim() || "Unknown";
  const statusText =
    r.status === "on_way" ? "On the way" : r.status === "declined" ? "Cannot attend" : "Waiting for response…";
  return (
    <div style={responderRowStyle}>
      <span style={{ ...leftRuleStyle, background: tone }} aria-hidden="true" />
      <span style={{ ...avatarStyle, background: avatarBg(r.status) }}>{r.user.initials}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: pending ? C.inkSoft : C.ink }}>
          {name}
        </div>
        <div
          style={{
            fontFamily: pending ? F.serif : F.body,
            fontStyle: pending ? "italic" : "normal",
            fontSize: pending ? 12 : 11.5,
            fontWeight: pending ? 600 : 700,
            marginTop: 2,
            color: tone,
          }}
        >
          {statusText}
        </div>
      </div>
      {r.status === "on_way" && r.etaMinutes != null && (
        <span style={etaPillStyle}>ETA {r.etaMinutes >= 60 ? "60+" : r.etaMinutes}m</span>
      )}
    </div>
  );
}

function avatarBg(status: Responder["status"]): string {
  if (status === "on_way") return `linear-gradient(135deg, ${FA.go}, ${FA.goLt})`;
  if (status === "declined") return `linear-gradient(135deg, ${FA.red}, ${FA.redDk})`;
  return `linear-gradient(135deg, ${FA.pending}, #C9B894)`;
}

// ---------------------------------------------------------------------------
// Resolve (State 6 footer) — the ONE confirmation modal (gotcha #12)
// ---------------------------------------------------------------------------

function ResolveButton({ busy, onResolve }: { busy: boolean; onResolve: () => void }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setConfirming(true)} disabled={busy} style={resolveBtnStyle}>
        Mark incident resolved
      </button>
      {confirming && (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Mark incident resolved">
          <div style={modalCardStyle}>
            <div style={modalTitleStyle}>Mark resolved?</div>
            <div style={modalBodyStyle}>All first aiders will be notified the incident is closed.</div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setConfirming(false)} style={modalCancelStyle}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  onResolve();
                }}
                style={modalConfirmStyle}
              >
                Yes, mark resolved
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const cardStyle: CSSProperties = {
  background: C.card,
  borderRadius: 16,
  overflow: "hidden",
  border: `1px solid ${FA.redLt}`,
  boxShadow: "0 4px 14px rgba(184,54,42,0.12)",
};

const cardHeaderStyle: CSSProperties = {
  background: `linear-gradient(180deg, ${FA.red} 0%, ${FA.redDk} 100%)`,
  color: "#FFFFFF",
  padding: "12px 16px",
};

const cardEyebrowRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
};

const cardEyebrowStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.95)",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const cardTimeStyle: CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.4,
  color: "rgba(255,255,255,0.85)",
};

const cardHeadlineStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 20,
  fontWeight: 700,
  letterSpacing: -0.3,
  color: "#FFFFFF",
  marginTop: 3,
  lineHeight: 1.1,
};

const fieldRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 0",
};

const fieldRowLabelStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 0.9,
  textTransform: "uppercase",
  color: FA.red,
  paddingTop: 2,
  flexShrink: 0,
};

const fieldRowValueStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 15,
  fontWeight: 600,
  color: C.ink,
  lineHeight: 1.3,
};

const phoneLinkStyle: CSSProperties = {
  display: "inline-block",
  marginTop: 2,
  fontFamily: F.mono,
  fontSize: 13,
  fontWeight: 700,
  color: FA.redDk,
  letterSpacing: 0.4,
  textDecoration: "none",
};

const badgeRedStyle: CSSProperties = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  background: FA.red,
  color: "#FFFFFF",
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.5,
};

const badgeMuteStyle: CSSProperties = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  background: C.paperWarm,
  color: C.inkSoft,
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.5,
};

const cannotBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: 52,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: "#FFFFFF",
  color: FA.stop,
  border: `1.5px solid ${FA.red}`,
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 14.5,
  fontWeight: 800,
  cursor: "pointer",
  letterSpacing: 0.2,
};

const goBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: 52,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(180deg, ${FA.go} 0%, #355C43 100%)`,
  color: "#FFFFFF",
  border: "none",
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 14.5,
  fontWeight: 800,
  cursor: "pointer",
  letterSpacing: 0.2,
  boxShadow: "0 3px 10px rgba(63,107,79,0.3)",
};

const miniTallyStyle: CSSProperties = {
  border: `1px dashed ${FA.redLt}`,
  background: "rgba(255,255,255,0.55)",
  borderRadius: 10,
  padding: "9px 12px",
  textAlign: "center",
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 13,
  color: FA.redDk,
};

const seeThreadBtnStyle: CSSProperties = {
  width: "100%",
  background: "none",
  border: "none",
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  color: C.inkSoft,
  cursor: "pointer",
  textDecoration: "underline",
  textUnderlineOffset: 3,
  padding: "4px 0",
};

const pickerStyle: CSSProperties = {
  background: FA.goBg,
  border: `1px solid ${FA.goLt}`,
  borderRadius: 14,
  padding: "14px 14px 16px",
};

const pickerEyebrowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  fontFamily: F.body,
  fontSize: 12.5,
  fontWeight: 800,
  color: FA.go,
  marginBottom: 12,
};

const pickerChipsStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginBottom: 14,
};

const etaChipStyle: CSSProperties = {
  flex: "1 0 auto",
  minWidth: 64,
  padding: "10px 12px",
  background: "#FFFFFF",
  border: `1px solid ${FA.goLt}`,
  borderRadius: 11,
  fontFamily: F.body,
  fontSize: 13.5,
  fontWeight: 800,
  color: FA.go,
  cursor: "pointer",
};

const etaChipSelStyle: CSSProperties = {
  ...etaChipStyle,
  background: FA.go,
  color: "#FFFFFF",
  border: `1px solid ${FA.go}`,
  boxShadow: "0 2px 8px rgba(63,107,79,0.3)",
};

const etaConfirmStyle: CSSProperties = {
  width: "100%",
  minHeight: 52,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(180deg, ${FA.go} 0%, #355C43 100%)`,
  color: "#FFFFFF",
  border: "none",
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 15,
  fontWeight: 900,
  letterSpacing: 0.3,
};

const etaEscapeStyle: CSSProperties = {
  width: "100%",
  background: "none",
  border: "none",
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  color: C.inkSoft,
  cursor: "pointer",
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

const summaryLineStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 16,
  fontWeight: 500,
  color: C.ink,
  lineHeight: 1.4,
  letterSpacing: -0.1,
};

const tallyRowStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 14,
  paddingTop: 14,
  borderTop: `1px solid ${C.hairlineLt}`,
};

const listStyle: CSSProperties = {
  background: C.card,
  borderRadius: 16,
  border: `1px solid ${C.hairline}`,
  overflow: "hidden",
  padding: "4px 0",
};

const responderRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "11px 14px 11px 16px",
  position: "relative",
};

const leftRuleStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  top: 8,
  bottom: 8,
  width: 3,
  borderRadius: 2,
};

const avatarStyle: CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 800,
  flexShrink: 0,
  letterSpacing: 0.3,
};

const etaPillStyle: CSSProperties = {
  background: FA.goBg,
  color: FA.go,
  fontFamily: F.mono,
  fontSize: 11.5,
  fontWeight: 700,
  padding: "4px 9px",
  borderRadius: 999,
  border: `1px solid ${FA.goLt}`,
  flexShrink: 0,
  letterSpacing: 0.3,
};

const overflowRowStyle: CSSProperties = {
  padding: "10px 16px",
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 13,
  color: C.inkMute,
  borderTop: `1px solid ${C.hairlineLt}`,
};

const emptyResponderStyle: CSSProperties = {
  padding: "16px",
  textAlign: "center",
  fontFamily: F.serif,
  fontStyle: "italic",
  color: C.inkMute,
};

const resolveBtnStyle: CSSProperties = {
  width: "100%",
  minHeight: 50,
  background: "#FFFFFF",
  color: FA.redDk,
  border: `1.5px solid ${FA.red}`,
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 14.5,
  fontWeight: 800,
  cursor: "pointer",
  letterSpacing: 0.2,
};

const resolvedNoteStyle: CSSProperties = {
  background: C.paperWarm,
  border: `1px solid ${C.hairline}`,
  borderRadius: 12,
  padding: "12px 14px",
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 13.5,
  color: C.inkSoft,
  textAlign: "center",
};

const modalOverlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(43,32,20,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  zIndex: 300,
};

const modalCardStyle: CSSProperties = {
  background: C.card,
  borderRadius: 18,
  padding: "20px 20px 18px",
  maxWidth: 360,
  width: "100%",
  boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
};

const modalTitleStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 20,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
};

const modalBodyStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 14,
  fontWeight: 600,
  color: C.inkSoft,
  marginTop: 6,
  lineHeight: 1.4,
};

const modalCancelStyle: CSSProperties = {
  flex: 1,
  minHeight: 46,
  background: "transparent",
  color: C.inkSoft,
  border: `1px solid ${C.hairline}`,
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
};

const modalConfirmStyle: CSSProperties = {
  flex: 1,
  minHeight: 46,
  background: `linear-gradient(180deg, ${FA.red} 0%, ${FA.redDk} 100%)`,
  color: "#FFFFFF",
  border: "none",
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 3px 10px rgba(184,54,42,0.35)",
};

const toastStyle: CSSProperties = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  bottom: "calc(24px + env(safe-area-inset-bottom))",
  background: C.ink,
  color: "#FFFCEC",
  fontFamily: F.body,
  fontWeight: 700,
  fontSize: 13.5,
  padding: "11px 18px",
  borderRadius: 999,
  boxShadow: "0 10px 28px rgba(0,0,0,0.25)",
  zIndex: 350,
  letterSpacing: 0.2,
  maxWidth: "90%",
  textAlign: "center",
};

const skeletonStyle: CSSProperties = {
  background: "rgba(255,255,255,0.6)",
  borderRadius: 16,
  border: `1px solid ${FA.redLt}`,
};

const errorCardStyle: CSSProperties = {
  background: C.card,
  border: `1px solid ${FA.redLt}`,
  borderRadius: 12,
  padding: "16px",
  fontFamily: F.body,
  fontWeight: 700,
  color: FA.redDk,
  textAlign: "center",
};

const srOnlyStyle: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};
